import { coordinatesToGisCoordinates, dateRangeToDateArray } from '@curbnturf/helpers';
import { DateTime } from 'luxon';
import { IListing, IRoadRestriction, ISearchableListing, IVehicle } from '../interfaces';
import { earliestBookableDate } from '../utils/reservation';
import { AmenityList } from './amenity-list';
import { CoordinateGis } from './coordinates-gis';
import { Listing } from './listing';
import { Photo } from './photo';
import { RoadCondition } from './road-condition';

export class RoadRestriction implements IRoadRestriction {
  maxHeight?: number; // ft
  maxWidth?: number; // ft
  maxLength?: number; // ft
  minClearance?: number; // in
  roadCondition: RoadCondition;
}

export class SearchableListing implements ISearchableListing {
  id: number;

  // These coordinates should have reduced accuracy to so the listing data can be public
  coordinates: CoordinateGis;
  name: string;
  description: string;

  city?: string;
  state?: string;

  lowPrice?: number;
  highPrice?: number;
  availableDays?: {
    date: string;
    price: number;
  }[];

  availableSiteCount?: number;

  extrasCount?: number;

  minLengthOfStayInDays?: number;
  maxLengthOfStayInDays?: number;
  reserveDaysInAdvance?: number;
  photos?: Photo[];
  maxHeight?: number; // ft
  maxWidth?: number; // ft
  maxLength?: number; // ft
  vehicles?: IVehicle;

  roadRestrictions: RoadRestriction[];

  amenities: AmenityList;

  extraTents?: {
    price: number;
    count: number;
  };

  extraVehicles?: {
    price: number;
    count: number;
  };

  rating: number;
  ratingCount: number;

  static fromListing(startingListing: IListing): SearchableListing | undefined {
    // ensure the listing is propertly initialized
    const listing = new Listing(startingListing);

    if (listing.deleted || !listing.available || !listing.coordinates?.lat || !listing.coordinates?.lon) {
      return;
    }

    const firstPossiblyAvailableDay = DateTime.fromISO(earliestBookableDate(listing));
    const lastPossiblyAvailableDay = DateTime.now().plus({ days: listing.bookingWindow || 0 });

    const possiblyAvailableDays = dateRangeToDateArray(firstPossiblyAvailableDay, lastPossiblyAvailableDay).map(
      (date) => date.toISODate(),
    );

    const busyDays = listing.getBusyDays();

    const availableDays = possiblyAvailableDays.reduce(
      (actualDays, day) => {
        if (day && !busyDays.includes(day)) {
          // For simplicity, we are using the listing's base price for all available days.
          actualDays.push({ date: day, price: listing.getMaxPrice() });
        }

        return actualDays;
      },
      [] as { date: string; price: number }[],
    );

    const baseVehicles = {
      classA: false,
      classB: false,
      classC: false,
      fifthWheel: false,
      travelTrailer: false,
      truckCamper: false,
      teardrop: false,
      popup: false,
    };

    return new SearchableListing({
      id: listing.id as number,
      coordinates: coordinatesToGisCoordinates(listing.coordinates),
      name: listing.name || '',
      description: listing.description || '',
      city: listing.address ? listing.address.city : undefined,
      state: listing.address ? listing.address.state : undefined,
      photos: listing.photos,
      maxHeight: listing.sites ? Math.max(...listing.sites.map((site) => site.maxHeight || 0)) : undefined,
      maxWidth: listing.sites ? Math.max(...listing.sites.map((site) => site.maxWidth || 0)) : undefined,
      maxLength: listing.sites ? Math.max(...listing.sites.map((site) => site.maxLength || 0)) : undefined,
      roadRestrictions:
        listing.getAccumulatedAccessRoutes()?.map((route) => ({
          maxHeight: route.maxHeight,
          maxWidth: route.maxWidth,
          maxLength: route.maxLength,
          minClearance: route.minClearance,
          roadCondition: { ...route.roadCondition },
        })) || [],
      amenities: AmenityList.fromAmenities(listing.getAccumulatedAmenities()),
      rating: listing.getRating(),
      ratingCount: listing.getRatingCount(),
      lowPrice: listing.getMinPrice(),
      highPrice: listing.getMaxPrice(),
      price: listing.getMinPrice(),
      vehicles: listing.sites
        ? listing.sites.reduce(
            (combinedVehicles, site) => {
              const vehicles = site.vehicles;
              return {
                classA: combinedVehicles.classA || vehicles?.classA || false,
                classB: combinedVehicles.classB || vehicles?.classB || false,
                classC: combinedVehicles.classC || vehicles?.classC || false,
                fifthWheel: combinedVehicles.fifthWheel || vehicles?.fifthWheel || false,
                travelTrailer: combinedVehicles.travelTrailer || vehicles?.travelTrailer || false,
                truckCamper: combinedVehicles.truckCamper || vehicles?.truckCamper || false,
                teardrop: combinedVehicles.teardrop || vehicles?.teardrop || false,
                popup: combinedVehicles.popup || vehicles?.popup || false,
              };
            },
            { ...baseVehicles },
          )
        : { ...baseVehicles },
      availableDays,
      availableSiteCount: listing.sites ? listing.sites.filter((site) => site.available).length : 1,
      extrasCount: listing.extras ? listing.extras.length : 0,
    });
  }

  constructor(data?: ISearchableListing) {
    if (data) {
      this.setData(data);
    }
  }

  setData(data: ISearchableListing): void {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[key] = (data as any)[key];
      }
    }
  }
}
